// =============================================================================
// SimSense Platform — Public Landing Page
// =============================================================================
// Marketing/overview page for unauthenticated visitors.
// Shows product overview, features, pricing, and CTAs to sign up/login.

import { useEffect } from "react";
import { Link } from "react-router-dom";
import "./Landing.css";

export function Landing() {
  useEffect(() => {
    document.title = "SimSense | iRacing Telemetry & Coaching";
  }, []);

  return (
    <div className="landing">
      {/* ---------- Navigation ---------- */}
      <nav className="landing-nav">
        <div className="landing-logo">
          <div className="landing-logo-mark">S</div>
          <span className="landing-logo-text">SimSense</span>
        </div>
        <div className="landing-nav-actions">
          <Link to="/pricing" className="landing-nav-link">Pricing</Link>
          <Link to="/login" className="landing-nav-link">Sign In</Link>
          <Link to="/signup" className="landing-nav-cta">Get Started Free</Link>
        </div>
      </nav>

      {/* ---------- Hero ---------- */}
      <section className="landing-hero">
        <h1>
          Your Racing Data,{" "}
          <span className="accent">Decoded</span>
        </h1>
        <p className="landing-hero-subtitle">
          SimSense transforms your iRacing telemetry into actionable insights.
          Advanced telemetry coaching, lap-by-lap analysis, and setup intelligence
          to help you find speed you didn't know you had.
        </p>
        <div className="landing-hero-actions">
          <Link to="/signup" className="landing-btn-primary">
            Get Started Free
          </Link>
          <Link to="/login" className="landing-btn-secondary">
            Sign In
          </Link>
        </div>

        <div className="landing-hero-image">
          <span>App screenshot (coming soon)</span>
        </div>
      </section>

      {/* ---------- Features ---------- */}
      <section className="landing-section">
        <div className="landing-section-header">
          <h2>Everything You Need to Get Faster</h2>
          <p>
            From raw telemetry to personalized coaching. SimSense handles
            the data so you can focus on driving.
          </p>
        </div>

        <div className="landing-features-grid">
          <div className="landing-feature-card">
            <div className="landing-feature-icon" style={{ background: "var(--accent-dim)", color: "var(--accent-hover)" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20V10" /><path d="M18 20V4" /><path d="M6 20v-4" />
              </svg>
            </div>
            <h3>Telemetry Analysis</h3>
            <p>
              Deep dive into speed, throttle, braking, and steering data across
              every lap. Spot patterns and problem areas at a glance.
            </p>
          </div>

          <div className="landing-feature-card">
            <div className="landing-feature-icon" style={{ background: "var(--good-dim)", color: "var(--good)" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h3>Advanced Coaching</h3>
            <p>
              Get personalized coaching reports powered by advanced telemetry analysis.
              Actionable recommendations for braking points, throttle application,
              steering input, and countless other data points.
            </p>
          </div>

          <div className="landing-feature-card">
            <div className="landing-feature-icon" style={{ background: "var(--warn-dim)", color: "var(--warn)" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 3h5v5" /><path d="M8 3H3v5" /><path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" /><path d="m15 9 6-6" />
              </svg>
            </div>
            <h3>Lap Comparison</h3>
            <p>
              Compare laps side-by-side with interactive graphs, track map,
              and more. See exactly where you're gaining or losing time.
            </p>
          </div>

          <div className="landing-feature-card">
            <div className="landing-feature-icon" style={{ background: "rgba(168, 85, 247, 0.15)", color: "#a855f7" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" />
              </svg>
            </div>
            <h3>Corner Analysis</h3>
            <p>
              Break down every corner with entry speed, apex timing, and exit
              acceleration. A-F grades show where to focus your practice.
            </p>
          </div>

          <div className="landing-feature-card">
            <div className="landing-feature-icon" style={{ background: "rgba(236, 72, 153, 0.15)", color: "#ec4899" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <h3>Teams & Leaderboards</h3>
            <p>
              Create or join a team. Share telemetry and setups with teammates.
              Compete on community leaderboards across every track and car combo.
            </p>
          </div>

          <div className="landing-feature-card">
            <div className="landing-feature-icon" style={{ background: "rgba(20, 184, 166, 0.15)", color: "#14b8a6" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
            </div>
            <h3>Desktop Agent</h3>
            <p>
              Install the SimSense desktop app and your telemetry files upload
              automatically in the background after every session.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- How It Works ---------- */}
      <section className="landing-section landing-section--shaded">
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div className="landing-section-header">
            <h2>How It Works</h2>
            <p>
              From .ibt file to coaching report in minutes, not hours.
            </p>
          </div>

          <div className="landing-steps">
            <div className="landing-step">
              <div className="landing-step-number">1</div>
              <h3>Upload</h3>
              <p>
                Let the desktop agent upload them automatically after every session.
                Or drag and drop your .ibt telemetry files for previous sessions.
              </p>
            </div>
            <div className="landing-step">
              <div className="landing-step-number">2</div>
              <h3>Analyze</h3>
              <p>
                SimSense processes your telemetry data automatically, extracting
                lap times, stints, tire data, corner performance, and more.
              </p>
            </div>
            <div className="landing-step">
              <div className="landing-step-number">3</div>
              <h3>Improve</h3>
              <p>
                Get advanced coaching, compare laps visually, and track your
                progress over time. Find seconds, not tenths.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- App Preview ---------- */}
      <section className="landing-section">
        <div className="landing-section-header">
          <h2>Built for Sim Racers</h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", maxWidth: "1000px", margin: "0 auto" }}>
          <div className="landing-screenshot">
            <span>Telemetry Dashboard</span>
          </div>
          <div className="landing-screenshot">
            <span>Lap Comparison</span>
          </div>
          <div className="landing-screenshot">
            <span>Coaching Reports</span>
          </div>
          <div className="landing-screenshot">
            <span>Corner Analysis</span>
          </div>
        </div>
      </section>

      {/* ---------- Pricing ---------- */}
      <section className="landing-section landing-section--shaded" id="pricing">
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div className="landing-section-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>
              Start free. Upgrade when you're ready to take your racing to the next level.
            </p>
          </div>

          <div className="landing-pricing-grid">
            {/* Free Tier */}
            <div className="landing-price-card">
              <h3>Free</h3>
              <div className="price">$0<span> / month</span></div>
              <p className="price-desc">Core telemetry tools, forever free</p>
              <ul className="landing-price-features">
                <li><span className="check">&#10003;</span> Unlimited session uploads</li>
                <li><span className="check">&#10003;</span> Full telemetry data extraction</li>
                <li><span className="check">&#10003;</span> Lap comparison with track map</li>
                <li><span className="check">&#10003;</span> Run averages & stints analysis</li>
                <li><span className="check">&#10003;</span> Tire temperature & wear data</li>
                <li><span className="check">&#10003;</span> Corner-by-corner breakdown</li>
                <li><span className="check">&#10003;</span> Car and track community leaderboards</li>
                <li><span className="check">&#10003;</span> Team creation & data sharing</li>
                <li><span className="check">&#10003;</span> Desktop program and web client</li>
                <li><span className="check">&#10003;</span> 6 month data retention</li>
                <li><span className="check">&#10003;</span> Basic coaching observations</li>
                <li><span className="check">&#10003;</span> 3 coaching reports / month</li>
              </ul>
              <Link to="/signup" className="landing-price-btn secondary">
                Get Started
              </Link>
            </div>

            {/* Pro Tier */}
            <div className="landing-price-card featured">
              <h3>SimSense Pro</h3>
              <div className="price">$5<span> / month</span></div>
              <p className="price-desc">For drivers serious about improvement</p>
              <ul className="landing-price-features">
                <li><span className="check">&#10003;</span> Everything in Free, plus:</li>
                <li><span className="check">&#10003;</span> Unlimited advanced coaching reports</li>
                <li><span className="check">&#10003;</span> Corner grading system (A-F) per session</li>
                <li><span className="check">&#10003;</span> Fastest driver lap comparison</li>
              </ul>
              <Link to="/signup" className="landing-price-btn primary">
                Start Free Trial
              </Link>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: "32px" }}>
            <Link to="/pricing" style={{ color: "var(--accent-hover)", fontSize: "14px", fontWeight: 600, textDecoration: "none" }}>
              See full feature comparison &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className="landing-section" style={{ textAlign: "center" }}>
        <h2 style={{ fontSize: "clamp(28px, 3.5vw, 42px)", fontWeight: 800, letterSpacing: "-0.025em", margin: "0 0 16px" }}>
          Ready to Find More Speed?
        </h2>
        <p style={{ fontSize: "17px", color: "var(--text2)", maxWidth: "480px", margin: "0 auto 32px", lineHeight: 1.6 }}>
          Join SimSense and start turning your telemetry data into faster lap times today.
        </p>
        <Link to="/signup" className="landing-btn-primary">
          Create Your Free Account
        </Link>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="landing-footer">
        <div className="landing-footer-content">
          <div className="landing-footer-brand">SimSense</div>
          <div className="landing-footer-links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/login">Sign In</Link>
            <Link to="/signup">Get Started</Link>
          </div>
          <div className="landing-footer-links" style={{ marginTop: "8px", opacity: 0.6 }}>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/acceptable-use">Acceptable Use</Link>
            <Link to="/creator-agreement">Creator Agreement</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} SimSense. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
